// Scroll-state arithmetic for the filmstrip and the Bài nộp chip strip, kept pure so node --test can pin it.
// The strip used to hint at overflow with a gradient only; on an 86-inch panel read from 2-3 m a
// soft edge reads as a blurry border, not as "there is more", so the numbers below drive a real
// tappable control that also says HOW MUCH more - the document count is unbounded now that a
// teacher can generate quizzes onto the strip.

/** A cell's horizontal extent inside the scrolling track, in track coordinates. */
export interface StripCellBox {
  left: number;
  right: number;
}

export interface StripOverflow {
  /** Cells entirely past the left edge of the viewport. */
  before: number;
  /** Cells entirely past the right edge. */
  after: number;
}

/** A cell straddling the edge counts as reachable - only fully hidden cells are "more". */
const EDGE_TOLERANCE = 1;

export function stripOverflow(cells: StripCellBox[], scrollLeft: number, clientWidth: number): StripOverflow {
  const start = scrollLeft;
  const end = scrollLeft + clientWidth;
  let before = 0;
  let after = 0;
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i].right <= start + EDGE_TOLERANCE) before += 1;
    else if (cells[i].left >= end - EDGE_TOLERANCE) after += 1;
  }
  return { before, after };
}

/** Roughly one screenful, so a tap moves a page of thumbnails rather than a nudge or the lot. */
export const STRIP_PAGE_RATIO = 0.8;

/**
 * Where a page-scroll in `direction` (-1 start, 1 end) should land, clamped to the track.
 * Chrome 58 has no smooth `scrollTo(options)`, so the caller assigns `scrollLeft` directly.
 */
export function stripPageTarget(scrollLeft: number, clientWidth: number, scrollWidth: number, direction: number): number {
  const step = Math.max(1, Math.round(clientWidth * STRIP_PAGE_RATIO));
  const target = scrollLeft + direction * step;
  return Math.min(Math.max(target, 0), Math.max(0, scrollWidth - clientWidth));
}

/** Which ends still hide part of the track; an end scrolled flush shows no control. */
export function stripEdges(scrollLeft: number, clientWidth: number, scrollWidth: number): { start: boolean; end: boolean } {
  return { start: scrollLeft > EDGE_TOLERANCE, end: scrollWidth - clientWidth - scrollLeft > EDGE_TOLERANCE };
}

/** The smallest scroll that shows `cell` whole and clear of an edge control `margin` wide. */
export function stripRevealTarget(cell: StripCellBox, scrollLeft: number, clientWidth: number, scrollWidth: number, margin: number): number {
  let target = scrollLeft;
  if (cell.left - margin < scrollLeft) target = cell.left - margin;
  else if (cell.right + margin > scrollLeft + clientWidth) target = cell.right + margin - clientWidth;
  return Math.min(Math.max(target, 0), Math.max(0, scrollWidth - clientWidth));
}
